import { useCallback, useRef } from 'react';

/**
 * Custom hook to throttle function calls
 * @param {Function} callback - Function to throttle
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Throttled function
 */
export function useThrottle(callback, delay = 100) {
    const lastRan = useRef(null);
    const timeoutRef = useRef(null);

    return useCallback(
        (...args) => {
            const now = Date.now();

            // Initialize lastRan on first call
            if (lastRan.current === null) {
                lastRan.current = now;
            }

            const timeSinceLastRun = now - lastRan.current;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            if (timeSinceLastRun >= delay) {
                lastRan.current = now;
                callback(...args);
            } else {
                timeoutRef.current = setTimeout(() => {
                    lastRan.current = Date.now();
                    callback(...args);
                }, delay - timeSinceLastRun);
            }
        },
        [callback, delay],
    );
}
